import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiHome,
  FiPieChart,
  FiShield,
  FiRepeat,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiDownload,
  FiUsers,
  FiClipboard,
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import ThemeSelector from "./ThemeSelector";

const Sidebar = ({ collapsed, onToggleCollapse, width }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [dashboardExpanded, setDashboardExpanded] = useState(false);

  const isActive = (path) => location.pathname === path;

  const isDashboardPathActive = () => {
    return location.pathname === "/dashboard" ||
           location.pathname === "/dashboard/leadership" ||
           location.pathname === "/dashboard/organization";
  };

  // Auto-expand dashboard submenu when any dashboard path is active
  useEffect(() => {
    if (isDashboardPathActive()) {
      setDashboardExpanded(true);
    }
  }, [location.pathname]);

  // Role-derived flags. Kept in one place so the nav + sub-items stay aligned.
  // Mirror of backend `_role_defaults`:
  //   admin / superadmin → everything
  //   c_level            → main + leadership
  //   senior_leader      → leadership only
  //   manager            → main + my_team
  //   employee           → nothing
  const role = user?.role;
  const isAdminLike =
    user?.is_admin || ["admin", "superadmin"].includes(role);
  const hasOverview = isAdminLike || ["c_level", "manager"].includes(role);
  const hasLeadership = isAdminLike || ["c_level", "senior_leader"].includes(role);
  const hasAnyDashboard = hasOverview || hasLeadership;
  const isPureAdmin = user?.is_admin || ["admin", "superadmin"].includes(role);

  const navItems = [
    { path: "/welcome", label: "Home", icon: FiHome, show: true },
    {
      path: "/dashboard",
      label: "AI Dashboard",
      icon: FiPieChart,
      show: hasAnyDashboard,
    },
    {
      // Reports page is now admin/superadmin only. Non-admin leaders use
      // the inline download buttons on the Leadership tab instead, which
      // hit the subtree-scoped /api/reports/team/{id} endpoint.
      path: "/reports",
      label: "Reports",
      icon: FiDownload,
      show: isPureAdmin,
    },
    { path: "/admin", label: "Admin Panel", icon: FiShield, show: isPureAdmin },
    { path: "/admin/retake", label: "Retake", icon: FiRepeat, show: isPureAdmin },
    { path: "/admin/access", label: "Access Control", icon: FiUsers, show: isPureAdmin },
    { path: "/admin/assessments", label: "Assessments", icon: FiClipboard, show: isPureAdmin },
  ];

  const visibleItems = navItems.filter((item) => item.show);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const btnBase = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: collapsed ? "10px 0" : "10px 12px",
    justifyContent: collapsed ? "center" : "flex-start",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500",
    width: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    transition: "color 0.15s, background-color 0.15s",
    backgroundColor: "transparent",
  };

  return (
    <motion.aside
      animate={{ width }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        height: "100vh",
        backgroundColor: "var(--bg-primary)",
        borderRight: "1px solid var(--border-subtle)",
        display: "flex",
        flexDirection: "column",
        zIndex: 100,
        overflowX: "visible",
        overflowY: "auto",
      }}
    >
      {/* Logo */}
      <div
        style={{
          padding: "16px",
          borderBottom: "1px solid var(--border-subtle)",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          minHeight: "56px",
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        <img
          src="/aiq-logo.svg"
          alt="AIQ"
          style={{
            width: "32px",
            height: "32px",
            flexShrink: 0,
          }}
        />
        {!collapsed && (
          <span style={{ fontSize: "15px", fontWeight: "700", color: "var(--text-primary)" }}>
            AIQ
          </span>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "12px 8px", display: "flex", flexDirection: "column", gap: "2px" }}>
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          const isDashboard = item.path === "/dashboard";
          const dashboardActive = isDashboard && isDashboardPathActive();

          // Dashboard submenu items — role-driven so each user only sees
          // what their role grants. Organization is intentionally excluded
          // from the menu for now; the route still exists if an admin
          // navigates by URL.
          const dashboardSubItems = [
            ...(hasOverview
              ? [{ path: "/dashboard", label: "Overview" }]
              : []),
            ...(hasLeadership
              ? [{ path: "/dashboard/leadership", label: "Leadership" }]
              : []),
            // Organization tab hidden for now — uncomment to bring it back:
            // { path: "/dashboard/organization", label: "Organization" },
          ];

          return (
            <div key={item.path} style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {/* Main nav item */}
              <button
                onClick={() => {
                  if (isDashboard) {
                    setDashboardExpanded(!dashboardExpanded);
                  } else {
                    navigate(item.path);
                  }
                }}
                style={{
                  ...btnBase,
                  fontWeight: dashboardActive || active ? "600" : "500",
                  color: dashboardActive || active ? "var(--color-highlight)" : "var(--text-secondary)",
                  backgroundColor: dashboardActive || active ? "var(--color-highlight-soft)" : "transparent",
                }}
                onMouseEnter={(e) => {
                  if (!dashboardActive && !active) e.currentTarget.style.backgroundColor = "var(--bg-card)";
                }}
                onMouseLeave={(e) => {
                  if (!dashboardActive && !active) e.currentTarget.style.backgroundColor = "transparent";
                }}
                title={collapsed ? item.label : undefined}
              >
                <Icon size={18} style={{ flexShrink: 0 }} />
                {!collapsed && <span style={{ flex: 1, textAlign: "left" }}>{item.label}</span>}
                {!collapsed && isDashboard && (
                  <FiChevronDown
                    size={16}
                    style={{
                      flexShrink: 0,
                      transform: dashboardExpanded ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                )}
              </button>

              {/* Dashboard submenu */}
              {isDashboard && !collapsed && dashboardExpanded && (
                <div style={{ display: "flex", flexDirection: "column", gap: "2px", paddingLeft: "8px" }}>
                  {dashboardSubItems.map((subItem) => {
                    const subActive = isActive(subItem.path);
                    return (
                      <button
                        key={subItem.path}
                        onClick={() => navigate(subItem.path)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "8px 12px",
                          justifyContent: "flex-start",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: subActive ? "600" : "400",
                          width: "100%",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          transition: "color 0.15s, background-color 0.15s",
                          backgroundColor: "transparent",
                          color: subActive ? "var(--color-highlight)" : "var(--text-secondary)",
                          marginLeft: "4px",
                          borderLeft: subActive ? "2px solid var(--color-highlight)" : "2px solid transparent",
                          paddingLeft: "10px",
                        }}
                        onMouseEnter={(e) => {
                          if (!subActive) e.currentTarget.style.backgroundColor = "var(--bg-card)";
                        }}
                        onMouseLeave={(e) => {
                          if (!subActive) e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {subItem.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Bottom */}
      <div style={{ padding: "8px", borderTop: "1px solid var(--border-subtle)", display: "flex", flexDirection: "column", gap: "2px" }}>
        {/* Theme */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "4px 4px" }}>
          <ThemeSelector />
          {!collapsed && (
            <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--text-secondary)" }}>
              Theme
            </span>
          )}
        </div>

        {/* Collapse */}
        <button
          onClick={onToggleCollapse}
          style={{ ...btnBase, color: "var(--text-secondary)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--bg-card)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
        >
          {collapsed ? <FiChevronRight size={18} /> : <FiChevronLeft size={18} />}
          {!collapsed && <span>Collapse</span>}
        </button>

        {/* Logout */}
        <button
          onClick={handleLogout}
          style={{ ...btnBase, color: "var(--color-incorrect)" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(239,68,68,0.08)")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          title={collapsed ? "Sign Out" : undefined}
        >
          <FiLogOut size={18} style={{ flexShrink: 0 }} />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>
    </motion.aside>
  );
};

export default Sidebar;
